import { EnvironmentalType } from "enum/EnvironmentalType";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  IReportServicesParams,
  ReportServiceData,
  UnitMeasurement,
} from "services/ReportService";
import {
  ComposedUnits,
  Units,
} from "stores/data/unitMeasurement/UnitMeasurementStore";
import { useStores } from "stores/setup/use-store";
import { Column, ColumnInput, Row, ThresholdItem, TableData } from "../types";
import { intervalTime } from "../utils";

export function useForecastTableData(selectedStation: string) {
  const [tableData, setTableData] = useState<TableData>({
    columns: [],
    rows: [],
  });
  const abortControllerRef = useRef<AbortController | null>(null);

  const {
    dataStores: { forecastChartPageStore, unitMeasurementStore },
  } = useStores();

  useEffect(() => {
    unitMeasurementStore.getUserUnitMeasurementApi();
  }, []);

  const getType = useCallback((key: string) => {
    if (
      key.startsWith("wind") ||
      key.startsWith("precip") ||
      key.startsWith("temp_air") ||
      key.startsWith("slp") ||
      key.startsWith("visibility") ||
      key.startsWith("gust") ||
      key.startsWith("cloud_base_height") ||
      key.startsWith("icing_probability") ||
      key.startsWith("precip_accum_")
    ) {
      return EnvironmentalType.WEATHER;
    }
    if (
      key.startsWith("wave_height") ||
      key.startsWith("peak_period") ||
      key.startsWith("mean_period") ||
      key.startsWith("wave_dir") ||
      key.startsWith("wave_power")
    ) {
      if (key.includes("_sea")) return EnvironmentalType.WAVE_SEA;
      if (key.includes("_swell")) return EnvironmentalType.WAVE_SWELL;
      return EnvironmentalType.WAVE;
    }
    if (key.startsWith("water_level") || key.startsWith("current")) {
      return EnvironmentalType.WATER_FLOW;
    }
    if (key.startsWith("temp_water")) {
      return EnvironmentalType.WATER_PROPERTIES;
    }
    return null;
  }, []);

  const getColumns = useCallback(
    (data: ReportServiceData): Column[] => {
      const showThresholdsList = [
        "wind_dir",
        "wind_vel",
        "gust",
        "wind_vel_80m",
        "gust_80m",
        "wind_vel_100m",
        "gust_100m",
        "precip",
        "temp_air",
        "temp_sea",
        "slp",
        "visibility",
        "wave_height",
        "wave_height_max",
        "peak_period",
        "mean_period",
        "wave_dir",
        "wave_height_sea",
        "peak_period_sea",
        "wave_dir_sea",
        "wave_height_swell",
        "peak_period_swell",
        "wave_dir_swell",
        "wave_power",
        "water_level",
        "current_vel",
        "current_dir",
        "temp_water",
        "cloud_base_height",
        "icing_probability",
        "precip_accum_12h",
        "precip_accum_24h",
        "precip_accum_48h",
        "precip_accum_72h",
        "precip_accum_96h",
        "precip_accum_120h",
      ];

      const columns: Column[] = [
        {
          type: "date",
          label: "",
          inputs: [
            {
              label: "Date/Time",
              key: "dates",
              showThresholds: false,
              unit: data.tabularForecastData.reportForecastData.timezone,
              unitSymbol: data.tabularForecastData.reportForecastData.timezone,
            },
          ],
        },
        ...data.tabularForecastData.reportColumns.map((column) => {
          if (column === "visibility") {
            return {
              type: "visibility",
              label: "visibility",
              inputs: [
                {
                  label: "",
                  key: "",
                  showThresholds: false,
                  unit: null,
                  unitSymbol: null,
                },
              ],
            };
          }

          const inputEntries = Object.entries(
            data.tabularForecastData.reportForecastData.data,
          );

          const inputs: ColumnInput[] = inputEntries
            .map(([key, value]: [string, unknown]) => {
              const typedValue = value as (number | null)[];
              const type = getType(key);
              if (
                type === column &&
                typedValue.length > 0 &&
                !typedValue.every((v) => v === null)
              ) {
                const typedKey =
                  key as keyof typeof data.tabularForecastData.reportForecastData.unitMeasurement;
                const unit =
                  data.tabularForecastData.reportForecastData.unitMeasurement?.[
                    typedKey
                  ]?.units ?? null;

                const unitSymbol =
                  data.tabularForecastData.reportForecastData.unitMeasurement?.[
                    typedKey
                  ]?.units_symbol ?? null;

                const userUnit = unitMeasurementStore.unitMeasurementConfig[
                  key
                ] as Units;

                return {
                  label: key,
                  key,
                  unit: unitMeasurementStore.unitMeasurementConfig[key] || unit,
                  showThresholds: showThresholdsList.includes(key),
                  unitSymbol: userUnit ?? unitSymbol,
                } as ColumnInput;
              }

              return null;
            })
            .filter((input): input is ColumnInput => input !== null);

          return {
            type: column,
            label: column,
            inputs,
          };
        }),
      ];

      const filteredColumns = columns.filter(
        (column) => column.type !== "visibility" && column.inputs.length > 0,
      );

      return filteredColumns;
    },
    [unitMeasurementStore.unitMeasurementConfig, getType],
  );

  const getRows = useCallback(
    (data: ReportServiceData): Row[][] => {
      const dataWithDates: { [key: string]: (number | string | null)[] } = {
        dates: data.tabularForecastData.reportForecastData.dates,
        ...data.tabularForecastData.reportForecastData.data,
      };

      const filteredKeys = Object.keys(dataWithDates).filter((key) =>
        dataWithDates[key].some(
          (value) => value !== null && value !== undefined,
        ),
      );

      const maxLength = Math.max(
        ...filteredKeys.map((key) => dataWithDates[key].length),
      );

      const rows: Row[][] = [];
      for (let i = 0; i < maxLength; i++) {
        const dateValue = dataWithDates.dates?.[i];
        const rowId = `row-${dateValue || i}`;

        const row = filteredKeys.map((key) => {
          const value = dataWithDates[key][i] as number;
          const unitFromData =
            data.tabularForecastData.reportForecastData.unitMeasurement[
              key as keyof UnitMeasurement
            ];

          const userUnit = unitMeasurementStore.unitMeasurementConfig[
            key
          ] as Units;

          const haveUnit = !!unitFromData?.units_symbol && !!userUnit;
          const isTheSameUnit = unitFromData?.units_symbol === userUnit;
          const isDegress = unitFromData?.units === "degrees";

          if (unitFromData && unitFromData.units_symbol === "KT") {
            unitFromData.units_symbol = "kt";
          }

          const formattedData =
            haveUnit && !isDegress && !isTheSameUnit && value
              ? unitMeasurementStore.convertUnit(
                  value,
                  unitFromData?.units_symbol as ComposedUnits,
                  userUnit,
                )
              : value;

          const isPercentage =
            unitMeasurementStore.unitMeasurementConfig[key] === "%";

          const hasDecimalCase =
            formattedData?.toString()?.includes(".") ||
            formattedData?.toString()?.includes(",");

          const getRoundedValue = () => {
            if (isPercentage) {
              return Math.round(formattedData);
            }
            if (!hasDecimalCase) {
              return formattedData;
            }
            const rounded = formattedData?.toFixed(1);
            return rounded === "0.0" ? "0" : rounded;
          };

          const fixedValue = getRoundedValue();

          return {
            value:
              key !== "dates" && formattedData ? fixedValue : formattedData,
            key,
            id: rowId,
          };
        });
        rows.push(row);
      }

      return rows;
    },
    [unitMeasurementStore],
  );

  const getColorForValue = useCallback(
    (value: number | null, threshold: ThresholdItem) => {
      const parsedTresholdValue = parseFloat(threshold?.value || "0");
      if (
        !threshold?.value ||
        value === null ||
        value === undefined ||
        isNaN(value)
      )
        return "inherit";

      if (threshold.operator === ">") {
        if (value >= parsedTresholdValue) return "red.300";
        if (value < parsedTresholdValue && value >= parsedTresholdValue * 0.9)
          return "yellow.300";
        return "green.300";
      } else {
        if (value <= parsedTresholdValue) return "red.300";
        if (value > parsedTresholdValue && value <= parsedTresholdValue * 1.1)
          return "yellow.300";
        return "green.300";
      }
    },
    [],
  );

  const handleGetForecastReportData = useCallback(
    async (data: IReportServicesParams) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      await forecastChartPageStore.getForecastReportData(data, abortController);
      if (forecastChartPageStore.forecastReportData) {
        const columns = getColumns(forecastChartPageStore.forecastReportData);
        const rows = getRows(forecastChartPageStore.forecastReportData);

        setTableData({ columns, rows });
      }
      abortControllerRef.current = null;
    },
    [forecastChartPageStore, getColumns, getRows],
  );

  useEffect(() => {
    const [stationId, regionId] = selectedStation.split("-");
    if (stationId && regionId) {
      handleGetForecastReportData({
        regionId: Number(regionId),
        stationId: Number(stationId),
        forecastIntervalMinutes: 60,
      });
      const interval = setInterval(() => {
        handleGetForecastReportData({
          regionId: Number(regionId),
          stationId: Number(stationId),
          forecastIntervalMinutes: 60,
        });
      }, intervalTime);

      return () => {
        clearInterval(interval);
      };
    }
  }, [selectedStation, forecastChartPageStore, handleGetForecastReportData]);

  return {
    columns: tableData.columns,
    rows: tableData.rows,
    getColorForValue,
    getType,
  };
}
